import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, FileText, Database, ChevronDown, ChevronUp, Image, List, BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { pageContentAPI } from '../../utils/api';
import { ImageUploader } from './ImageUploader';

const PAGES = [
  { id: 'home', name: 'Home Page', icon: '🏠' },
  { id: 'housing-market', name: 'Housing Market', icon: '🏡' },
  { id: 'community-guide', name: 'Community Guide', icon: '👥' },
  { id: 'business-resources', name: 'Business Resources', icon: '💼' },
];

const CONTENT_TYPES = [
  { id: 'text', name: 'Text Block', icon: FileText },
  { id: 'image', name: 'Image', icon: Image },
  { id: 'stat', name: 'Statistics', icon: BarChart3 },
  { id: 'list', name: 'List/Items', icon: List },
  { id: 'rich', name: 'Rich Content', icon: FileText },
];

const emptyContent = {
  page: 'home',
  section: '',
  key: '',
  content_type: 'text',
  title: '',
  value: '',
  description: '',
  image: '',
  items: [],
  metadata: {},
  order: 0,
  active: true
};

export const PageContentManager = () => {
  const { toast } = useToast();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newContent, setNewContent] = useState(emptyContent);
  const [editForm, setEditForm] = useState(emptyContent);
  const [expandedId, setExpandedId] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      const page = selectedPage === 'all' ? null : selectedPage;
      const response = await pageContentAPI.getAllAdmin(page);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast({ title: "Error", description: "Failed to fetch page content", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [selectedPage, toast]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSeedContent = async () => {
    try {
      const response = await pageContentAPI.seed();
      toast({ title: "Success", description: response.data.message });
      fetchContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to seed content", variant: "destructive" });
    }
  };

  const handleCreate = async () => {
    if (!newContent.page || !newContent.section || !newContent.key) {
      toast({ title: "Error", description: "Page, section, and key are required", variant: "destructive" });
      return;
    }
    try {
      await pageContentAPI.create(newContent);
      toast({ title: "Content Created", description: "New content block has been added." });
      setNewContent(emptyContent);
      setShowAddForm(false);
      fetchContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create content", variant: "destructive" });
    }
  };

  const handleUpdate = async (id) => {
    try {
      await pageContentAPI.update(id, editForm);
      toast({ title: "Content Updated", description: "Content block has been updated." });
      setEditingId(null);
      fetchContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update content", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content block?')) return;
    try {
      await pageContentAPI.delete(id);
      toast({ title: "Content Deleted", description: "Content block has been removed." });
      fetchContent();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete content", variant: "destructive" });
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  // Group content by page
  const groupedContent = content.reduce((acc, item) => {
    if (!acc[item.page]) acc[item.page] = [];
    acc[item.page].push(item);
    return acc;
  }, {});

  const ContentForm = ({ data, setData, isEdit = false }) => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Page *</label>
          <select
            value={data.page}
            onChange={(e) => setData({ ...data, page: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            disabled={isEdit}
          >
            {PAGES.map(p => (
              <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Section *</label>
          <input
            type="text"
            value={data.section}
            onChange={(e) => setData({ ...data, section: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            placeholder="e.g., header, stats, neighborhoods"
            disabled={isEdit}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Key *</label>
          <input
            type="text"
            value={data.key}
            onChange={(e) => setData({ ...data, key: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            placeholder="e.g., page-title, market-stats"
            disabled={isEdit}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Content Type</label>
          <select
            value={data.content_type}
            onChange={(e) => setData({ ...data, content_type: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          >
            {CONTENT_TYPES.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Display Order</label>
          <input
            type="number"
            value={data.order}
            onChange={(e) => setData({ ...data, order: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
        <input
          type="text"
          value={data.title || ''}
          onChange={(e) => setData({ ...data, title: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          placeholder="Section or block title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
        <textarea
          value={data.description || ''}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          rows={3}
          placeholder="Description or body text"
        />
      </div>

      <ImageUploader
        value={data.image || ''}
        onChange={(url) => setData({ ...data, image: url })}
        label="Image (optional)"
      />

      {(data.content_type === 'stat') && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Statistics (JSON)</label>
          <textarea
            value={JSON.stringify(data.metadata || {}, null, 2)}
            onChange={(e) => {
              try {
                setData({ ...data, metadata: JSON.parse(e.target.value) });
              } catch {}
            }}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500 font-mono text-sm"
            rows={5}
            placeholder='{"stat1": "value1", "stat2": "value2"}'
          />
        </div>
      )}

      {(data.content_type === 'list' || data.content_type === 'rich') && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Items (JSON array)</label>
          <textarea
            value={JSON.stringify(data.items || [], null, 2)}
            onChange={(e) => {
              try {
                setData({ ...data, items: JSON.parse(e.target.value) });
              } catch {}
            }}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500 font-mono text-sm"
            rows={8}
            placeholder='[{"name": "Item 1", "description": "..."}, ...]'
          />
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id={`active-${isEdit ? 'edit' : 'new'}`}
          checked={data.active}
          onChange={(e) => setData({ ...data, active: e.target.checked })}
          className="w-4 h-4 mr-2"
        />
        <label htmlFor={`active-${isEdit ? 'edit' : 'new'}`} className="text-sm font-medium text-slate-700">
          Active (visible on site)
        </label>
      </div>
    </div>
  );

  const getTypeIcon = (type) => {
    const found = CONTENT_TYPES.find(t => t.id === type);
    if (found) {
      const Icon = found.icon;
      return <Icon className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  if (loading) {
    return <div className="text-center py-10">Loading page content...</div>;
  }

  return (
    <div className="space-y-6" data-testid="page-content-manager">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Page Content Manager</h2>
          <p className="text-slate-600">Edit text, images, and data on all pages</p>
        </div>
        <div className="flex gap-2">
          {content.length === 0 && (
            <Button onClick={handleSeedContent} variant="outline" className="border-blue-300 text-blue-600">
              <Database className="w-4 h-4 mr-2" /> Seed Default Content
            </Button>
          )}
          <Button onClick={() => setShowAddForm(!showAddForm)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Content Block
          </Button>
        </div>
      </div>

      {/* Page Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={selectedPage === 'all' ? 'default' : 'outline'}
          onClick={() => setSelectedPage('all')}
          size="sm"
        >
          All Pages
        </Button>
        {PAGES.map(page => (
          <Button
            key={page.id}
            variant={selectedPage === page.id ? 'default' : 'outline'}
            onClick={() => setSelectedPage(page.id)}
            size="sm"
          >
            {page.icon} {page.name}
          </Button>
        ))}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="border-2 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Add New Content Block
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <ContentForm data={newContent} setData={setNewContent} />
            <div className="flex gap-4 mt-6">
              <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" /> Save Content
              </Button>
              <Button variant="outline" onClick={() => { setShowAddForm(false); setNewContent(emptyContent); }}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content List */}
      {Object.keys(groupedContent).length === 0 ? (
        <Card className="border-2 border-slate-200">
          <CardContent className="py-10 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No page content found. Add content blocks or seed default data.</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedContent).map(([pageName, items]) => {
          const pageInfo = PAGES.find(p => p.id === pageName) || { name: pageName, icon: '📄' };
          return (
            <Card key={pageName} className="border-2 border-slate-200">
              <CardHeader className="bg-slate-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span>{pageInfo.icon}</span>
                  {pageInfo.name}
                  <Badge variant="outline" className="ml-2">{items.length} blocks</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {items.map((item) => (
                  <div key={item.id} className={`border rounded-lg ${!item.active ? 'bg-slate-50 border-slate-200' : 'border-emerald-200'}`}>
                    {editingId === item.id ? (
                      <div className="p-4">
                        <ContentForm data={editForm} setData={setEditForm} isEdit />
                        <div className="flex gap-4 mt-4">
                          <Button onClick={() => handleUpdate(item.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <Save className="w-4 h-4 mr-2" /> Save
                          </Button>
                          <Button variant="outline" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4 mr-2" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getTypeIcon(item.content_type)}
                              <span className="font-semibold text-slate-900">{item.title || item.key}</span>
                              <Badge variant="outline" className="text-xs">{item.section}</Badge>
                              {!item.active && <Badge variant="outline" className="text-slate-500">Inactive</Badge>}
                            </div>
                            {item.description && (
                              <p className="text-sm text-slate-600 line-clamp-2">{item.description}</p>
                            )}
                            {item.image && (
                              <img src={item.image} alt="" className="mt-2 h-16 rounded border" onError={(e) => e.target.style.display = 'none'} />
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button variant="outline" size="sm" onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                              {expandedId === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => startEdit(item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {expandedId === item.id && (
                          <div className="mt-4 pt-4 border-t text-sm space-y-2">
                            {item.metadata && Object.keys(item.metadata).length > 0 && (
                              <div>
                                <strong>Metadata:</strong>
                                <pre className="bg-slate-100 p-2 rounded mt-1 text-xs overflow-auto">{JSON.stringify(item.metadata, null, 2)}</pre>
                              </div>
                            )}
                            {item.items && item.items.length > 0 && (
                              <div>
                                <strong>Items ({item.items.length}):</strong>
                                <pre className="bg-slate-100 p-2 rounded mt-1 text-xs overflow-auto max-h-40">{JSON.stringify(item.items, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default PageContentManager;
